/**
 * The "Powered by Form-Magic.com" badge under a published form.
 *
 * Shown on every public form unless the account's plan carries
 * `hideAdvertisement` AND the owner switched it off for that form. On the
 * PUBLIC form that decision is the SERVER's and arrives as one boolean, so
 * nothing here knows or reveals which plan the form is on; `showsBadge`
 * below is the same rule for the places that are looking at their own
 * account anyway — the builder canvas — and is never what a respondent's
 * copy of the page relies on.
 *
 * Deliberately quiet: it sits outside the form card, never inside the
 * question flow, takes no colour from the owner's accent (an advert wearing
 * their brand would read as theirs), and is a plain link — no script, no
 * beacon, nothing that watches a respondent fill somebody else's form.
 */

import { useEffect, useState } from 'react';
import { PHONE, matchesNow } from '../shared/media';

/** Where the badge points, tagged so the traffic is attributable. */
export const FORM_MAGIC_URL = 'https://form-magic.com/?utm_source=form_badge&utm_medium=powered_by';

/**
 * Does a form show the badge? Both halves have to be true to hide it: the
 * owner asked, and their plan still carries the feature. Mirrors
 * `showsAdvertisement` on the server, which is the one that decides what a
 * respondent actually gets.
 */
export const showsBadge = (hideRequested?: boolean, entitled?: boolean): boolean =>
  !(hideRequested === true && entitled === true);

export default function FormMagicBadge({ floating = false, preview = false }: {
  /**
   * Sit above a full-screen gate instead of in the page flow. The gate
   * screens (password, invite, archived) paint an opaque backdrop over the
   * whole viewport, so a badge in the flow underneath it would be invisible.
   */
  floating?: boolean;
  /**
   * Draw it, but do not make it a way out. On the builder canvas this is a
   * picture of what a respondent will see — clicking it there should not
   * take the owner off to a marketing site mid-edit.
   */
  preview?: boolean;
}) {
  // Floating, it is fixed 18px off the bottom of the viewport — which on a
  // phone is over the middle of a modal card taller than the screen (M2/C1).
  // A respondent reading a closed-form notice or a thank-you should not have
  // an advert across it, so the badge steps aside until the card fits.
  const covered = useTallCard(floating);
  if (covered) return null;

  const inner = (
    <>
      <span className="of-ad-mark" aria-hidden="true">✦</span>
      <span>
        Powered by <b>Form-Magic.com</b> — build your own form, free
      </span>
    </>
  );
  return (
    <div className={`of-ad${floating ? ' of-ad-floating' : ''}`}>
      {preview ? (
        <span className="of-ad-link of-ad-preview" title="Respondents see this under your form. Settings → Form-Magic badge.">
          {inner}
        </span>
      ) : (
        <a className="of-ad-link" href={FORM_MAGIC_URL} target="_blank" rel="noopener noreferrer">
          {inner}
        </a>
      )}
    </div>
  );
}

/**
 * Is a modal card on screen taller than 80% of it? Only ever asked on a
 * phone, and only by the floating badge; jsdom and every desktop answer no
 * without measuring anything.
 */
function useTallCard(active: boolean): boolean {
  const [tall, setTall] = useState(false);
  useEffect(() => {
    if (!active || !matchesNow(PHONE)) { setTall(false); return; }
    const measure = () => {
      const card = document.querySelector('.of-modal-card') as HTMLElement | null;
      setTall(!!card && card.offsetHeight > window.innerHeight * 0.8);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active]);
  return tall;
}
